import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { CircleFlag } from "react-circle-flags";
import countryList from "react-select-country-list";
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";

export default function ClientPhoneField({
  disabled = false,
  initialValue = "",
  onChange,
  preferredCountry = "",
  preferredCountries = [],
  resetKey,
}) {
  const localInputRef = useRef(null);
  const initialValueRef = useRef(initialValue);
  const [query, setQuery] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [localNumber, setLocalNumber] = useState("");
  const [open, setOpen] = useState(false);

  const countryNamesByCode = useMemo(() => {
    const map = new Map();
    for (const item of countryList().getData()) {
      map.set(String(item.value || "").toUpperCase(), item.label);
    }
    return map;
  }, []);

  const phoneCountryOptions = useMemo(() => {
    return getCountries()
      .map((code) => ({
        code,
        name: countryNamesByCode.get(code) || code,
        callingCode: getCountryCallingCode(code),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [countryNamesByCode]);

  const selectedCallingCode = selectedCountry
    ? getCountryCallingCode(selectedCountry)
    : "";
  const selectedCountryName = selectedCountry
    ? countryNamesByCode.get(selectedCountry) || selectedCountry
    : "";
  const preferredCountryCodes = useMemo(() => {
    const rawValues = [
      preferredCountry,
      ...(Array.isArray(preferredCountries) ? preferredCountries : []),
    ];
    const seen = new Set();
    return rawValues
      .map((value) => String(value || "").trim().toUpperCase())
      .filter((code) => {
        if (!code || seen.has(code)) return false;
        seen.add(code);
        return true;
      });
  }, [preferredCountry, preferredCountries]);

  const filteredCountries = useMemo(() => {
    const raw = String(query || "").trim();
    if (!raw) {
      const preferredSet = new Set(preferredCountryCodes);
      const preferredOptions = preferredCountryCodes
        .map((code) => phoneCountryOptions.find((item) => item.code === code))
        .filter(Boolean);
      const rest = phoneCountryOptions.filter(
        (item) => !preferredSet.has(item.code)
      );
      return [...preferredOptions, ...rest].slice(0, 80);
    }

    const lower = raw.toLowerCase();
    const digits = raw.replace(/\D/g, "");

    return phoneCountryOptions
      .filter((item) => {
        if (digits) return item.callingCode.startsWith(digits);
        return (
          item.name.toLowerCase().includes(lower) ||
          item.code.toLowerCase().includes(lower)
        );
      })
      .slice(0, 80);
  }, [phoneCountryOptions, preferredCountryCodes, query]);
  const preferredVisibleCount = !String(query || "").trim()
    ? filteredCountries.filter((item) => preferredCountryCodes.includes(item.code)).length
    : 0;

  useEffect(() => {
    initialValueRef.current = initialValue;
  }, [initialValue]);

  function formatNationalDigits(value, countryCode) {
    const digits = String(value || "").replace(/\D/g, "");
    if (!digits || !countryCode) return digits;

    const callingCode = getCountryCallingCode(countryCode);
    const parsed = parsePhoneNumberFromString(`+${callingCode}${digits}`);
    if (parsed?.country === countryCode) {
      return parsed
        .formatInternational()
        .replace(new RegExp(`^\\+${callingCode}\\s*`), "");
    }

    return new AsYouType(countryCode).input(digits);
  }

  function applyParsedInternationalNumber(rawValue) {
    const parsed = parsePhoneNumberFromString(String(rawValue || ""));
    if (!parsed?.country || !parsed?.number || !parsed.nationalNumber) {
      return false;
    }

    setSelectedCountry(parsed.country);
    setLocalNumber(formatNationalDigits(parsed.nationalNumber, parsed.country));
    setQuery("");
    setOpen(false);
    onChange?.(parsed.number);
    window.setTimeout(() => localInputRef.current?.focus(), 0);
    return true;
  }

  useEffect(() => {
    const rawInitialValue = String(initialValueRef.current || "").trim();
    const parsed = parsePhoneNumberFromString(rawInitialValue);

    setQuery("");
    setOpen(false);

    if (parsed?.country) {
      setSelectedCountry(parsed.country);
      setLocalNumber(formatNationalDigits(parsed.nationalNumber, parsed.country));
      return;
    }

    const initialDigits = rawInitialValue.replace(/\D/g, "");
    const matchedByCallingCode = phoneCountryOptions
      .filter((item) => initialDigits.startsWith(item.callingCode))
      .sort((a, b) => b.callingCode.length - a.callingCode.length)[0];

    if (matchedByCallingCode) {
      setSelectedCountry(matchedByCallingCode.code);
      setLocalNumber(
        formatNationalDigits(
          initialDigits.slice(matchedByCallingCode.callingCode.length),
          matchedByCallingCode.code
        )
      );
      return;
    }

    setSelectedCountry("");
    setLocalNumber("");
    setQuery(rawInitialValue);
  }, [phoneCountryOptions, resetKey]);

  function selectCountry(code) {
    setSelectedCountry(code);
    setQuery("");
    setLocalNumber("");
    setOpen(false);
    onChange?.("");
    window.setTimeout(() => localInputRef.current?.focus(), 0);
  }

  function updateLocalNumber(nextValue) {
    const raw = String(nextValue || "").trim();
    const digits = raw.replace(/\D/g, "");
    setLocalNumber(formatNationalDigits(digits, selectedCountry));
    if (!selectedCountry || !digits) {
      onChange?.("");
      return;
    }

    const parsed = parsePhoneNumberFromString(digits, selectedCountry);
    onChange?.(parsed?.number || `+${selectedCallingCode}${digits}`);
  }

  function updateCountryQuery(nextValue) {
    if (/^\s*\+/.test(nextValue)) {
      const cleanedValue = nextValue.replace(/[^\d\s()+.-]/g, "");
      if (applyParsedInternationalNumber(cleanedValue)) {
        return;
      }
      setQuery(cleanedValue);
      setOpen(true);
      return;
    }

    setQuery(nextValue);
    setOpen(true);
  }

  function clearCountry() {
    setSelectedCountry("");
    setLocalNumber("");
    setQuery("");
    setOpen(false);
    onChange?.("");
  }

  if (selectedCountry) {
    return (
      <div className="clientPhoneCustomField clientPhoneCustomField--number">
        <button
          type="button"
          className="clientPhonePrefix"
          onClick={clearCountry}
          disabled={disabled}
          title={`Change country (${selectedCountryName})`}
        >
          <CircleFlag
            className="flagMutedImg"
            countryCode={selectedCountry.toLowerCase()}
            height="18"
          />
          <span>+{selectedCallingCode}</span>
        </button>
        <input
          ref={localInputRef}
          className="clientPhoneLocalInput"
          inputMode="tel"
          autoComplete="tel"
          value={localNumber}
          onChange={(e) =>
            updateLocalNumber(e.target.value.replace(/[^\d\s().-]/g, ""))
          }
          placeholder="Enter phone number"
          disabled={disabled}
        />
      </div>
    );
  }

  return (
    <div className="clientPhoneSearchBox">
      <input
        className="clientPhoneCustomField"
        value={query}
        onChange={(e) => updateCountryQuery(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onKeyDown={(e) => {
          if (e.key !== "Enter" || filteredCountries.length === 0) return;
          e.preventDefault();
          selectCountry(filteredCountries[0].code);
        }}
        placeholder="Search country or code"
        disabled={disabled}
      />

      {!disabled && open && filteredCountries.length > 0 && (
        <div className="clientPhoneCountryMenu">
          {filteredCountries.map((item, index) => (
            <Fragment key={item.code}>
              <button
                type="button"
                className={`clientPhoneCountryOption${
                  preferredVisibleCount > 0 && index < preferredVisibleCount
                    ? " clientPhoneCountryOption--preferred"
                    : ""
                }${
                  preferredVisibleCount > 0 &&
                  index === preferredVisibleCount - 1 &&
                  filteredCountries.length > preferredVisibleCount
                    ? " clientPhoneCountryOption--preferredLast"
                    : ""
                }`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectCountry(item.code)}
              >
                <CircleFlag
                  className="flagMutedImg"
                  countryCode={item.code.toLowerCase()}
                  height="18"
                />
                <span className="clientPhoneCountryName">{item.name}</span>
                <span className="clientPhoneCallingCode">
                  +{item.callingCode}
                </span>
              </button>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
